import React, { useState, useEffect, useCallback } from 'react';
import adminMagicResizeDebugService, {
  MagicResizeDebugResponse,
  DebugSizeResult,
  FinalVersionResult,
  GeminiBlueMarginStepResult,
  GeminiARConfigStepResult,
  GeminiImageOnlyStepResult,
  DebugHistoryItem,
} from '../../services/api/adminMagicResizeDebugService';
import adminMagicResizeService, {
  MagicResizeCategory,
} from '../../services/api/adminMagicResizeService';
import { TaskyBadge, TaskyButton, TaskyCard, TaskyInput, useConfirm } from '../tasky';
import { cn } from '../../lib/utils';

// --- Local presentational primitives (Tailwind + tasky tokens) ---

const SubTitle: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="mb-1.5 text-[13px] font-medium text-gray-500">{children}</div>
);

const ErrorBox: React.FC<{ children?: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'mb-3 rounded-tasky-xs border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-[13px] text-destructive',
      className,
    )}
  >
    {children}
  </div>
);

const ComparisonLabel: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="mb-1.5 flex items-center justify-center gap-1 text-xs font-semibold text-gray-700">
    {children}
  </div>
);

const ComparisonImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="max-h-[200px] max-w-full rounded-tasky-xs border border-gray-100 bg-gray-50 object-contain"
  />
);

const PlaceholderImage: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="flex h-[120px] items-center justify-center rounded-tasky-xs border border-dashed border-gray-300 bg-gray-50 p-2 text-center text-xs text-gray-400">
    {children}
  </div>
);

const ComparisonInfo: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="mt-1.5 text-[11px] leading-normal text-gray-500">{children}</div>
);

const DetailLabel: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="mb-0.5 text-[11px] font-medium text-gray-500">{children}</div>
);

const DetailValue: React.FC<{ children?: React.ReactNode; highlight?: boolean }> = ({
  children,
  highlight,
}) => (
  <div
    className={cn(
      'text-[13px]',
      highlight ? 'font-semibold text-destructive' : 'font-normal text-gray-900',
    )}
  >
    {children}
  </div>
);

const PromptText: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <pre className="mb-0 mt-1 max-h-[200px] overflow-y-auto whitespace-pre-wrap break-all rounded-tasky-xs border border-gray-200 bg-white p-2 text-xs text-gray-700">
    {children}
  </pre>
);

const STATUS_DOT_CLASS: Record<string, string> = {
  success: 'bg-success',
  error: 'bg-destructive',
};

const StatusDot: React.FC<{ status: string }> = ({ status }) => (
  <span className={cn('inline-block h-2 w-2 rounded-full', STATUS_DOT_CLASS[status] ?? 'bg-gray-400')} />
);

const MethodBadge: React.FC<{ success: boolean; children?: React.ReactNode }> = ({
  success,
  children,
}) => (
  <span
    className={cn(
      'inline-block h-[18px] w-[18px] rounded-tasky-xs text-center text-[10px] font-bold leading-[18px] text-white',
      success ? 'bg-success' : 'bg-destructive',
    )}
  >
    {children}
  </span>
);

const STRATEGY_BADGE_CLASS: Record<string, string> = {
  gemini: 'bg-blue-100 text-blue-700',
  no_crop_needed: 'bg-emerald-100 text-emerald-800',
};

// Card shells used by the image comparison grids
const COMPARISON_CARD = 'rounded-tasky-xs border border-gray-200 p-2 text-center';
const BLUE_MARGIN_CARD = 'rounded-tasky-xs border border-sky-200 bg-white p-2 text-center';
const APPROACH_CARD = 'rounded-tasky-xs border border-gray-200 bg-white p-2 text-center';
const FINAL_VERSION_CARD = 'rounded-tasky-xs border border-amber-200 bg-white p-2 text-center';

// Auto-fill detail grids
const DETAIL_GRID = 'mb-2 grid gap-2 grid-cols-[repeat(auto-fill,minmax(140px,1fr))]';
const DETAIL_GRID_NARROW = 'mb-2 grid gap-2 grid-cols-[repeat(auto-fill,minmax(120px,1fr))]';

// Method badge shells (Blue Margin / Approach A / Approach B)
const SECTION_BADGE = 'mr-1.5 inline-block rounded-tasky-xs px-1.5 py-0.5 text-[10px] font-semibold text-white';

// History table cells
const HISTORY_CELL = 'border-b border-gray-200 px-2.5 py-2 text-left';
const HISTORY_HEAD_CELL = 'border-b border-gray-200 bg-gray-100 px-2.5 py-2 text-left text-xs font-semibold text-gray-700';

const SMALL_OUTLINE_BUTTON =
  'h-7 border border-gray-300 bg-white px-3 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50';

const MagicResizeDebugPage: React.FC = () => {
  const confirm = useConfirm();
  // Input state
  const [imageId, setImageId] = useState<string>('');
  const [customWidth, setCustomWidth] = useState<string>('');
  const [customHeight, setCustomHeight] = useState<string>('');
  const [selectedSizes, setSelectedSizes] = useState<Array<{ width: number; height: number }>>([]);

  // Preset state
  const [presetCategories, setPresetCategories] = useState<MagicResizeCategory[]>([]);

  // Execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [results, setResults] = useState<MagicResizeDebugResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Expanded results
  const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());

  // History state
  const [historyItems, setHistoryItems] = useState<DebugHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLastPage, setHistoryLastPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingHistoryDetail, setIsLoadingHistoryDetail] = useState(false);
  const [loadedHistoryId, setLoadedHistoryId] = useState<number | null>(null);

  // Load presets
  const loadPresets = useCallback(async () => {
    try {
      const data = await adminMagicResizeService.getCategories();
      setPresetCategories(data);
    } catch (err) {
      console.error('Failed to load presets:', err);
    }
  }, []);

  const loadHistory = useCallback(async (page: number = 1) => {
    setIsLoadingHistory(true);
    try {
      const data = await adminMagicResizeDebugService.getHistory(page);
      setHistoryItems(data.data);
      setHistoryPage(data.current_page);
      setHistoryLastPage(data.last_page);
      setHistoryTotal(data.total);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Add custom size
  const handleAddCustomSize = () => {
    const w = parseInt(customWidth, 10);
    const h = parseInt(customHeight, 10);
    if (isNaN(w) || isNaN(h) || w < 10 || h < 10) return;
    if (selectedSizes.some(s => s.width === w && s.height === h)) return;
    setSelectedSizes(prev => [...prev, { width: w, height: h }]);
    setCustomWidth('');
    setCustomHeight('');
  };

  // Add preset size
  const handleAddPresetSize = (width: number, height: number) => {
    if (selectedSizes.some(s => s.width === width && s.height === height)) return;
    setSelectedSizes(prev => [...prev, { width, height }]);
  };

  // Remove size
  const handleRemoveSize = (index: number) => {
    setSelectedSizes(prev => prev.filter((_, i) => i !== index));
  };

  // Execute debug
  const handleExecute = async () => {
    const id = parseInt(imageId, 10);
    if (isNaN(id) || selectedSizes.length === 0) return;

    setIsExecuting(true);
    setError(null);
    setResults(null);

    try {
      const response = await adminMagicResizeDebugService.executeDebug({
        image_id: id,
        target_sizes: selectedSizes,
      });

      if (response.success) {
        setResults(response);
        // Expand all results by default
        setExpandedResults(new Set(response.results.map((_, i) => i)));
        loadHistory(1);
      } else {
        setError(response.error || 'Unknown error');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Request failed');
    } finally {
      setIsExecuting(false);
    }
  };

  const handleLoadHistoryDetail = async (id: number) => {
    setIsLoadingHistoryDetail(true);
    setLoadedHistoryId(id);
    try {
      const detail = await adminMagicResizeDebugService.getHistoryDetail(id);
      if (detail.result_data) {
        setResults(detail.result_data);
        setExpandedResults(new Set(detail.result_data.results.map((_: any, i: number) => i)));
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load history detail');
    } finally {
      setIsLoadingHistoryDetail(false);
      setLoadedHistoryId(null);
    }
  };

  const handleDeleteHistory = async (id: number) => {
    if (!(await confirm({ title: 'Delete this history entry?', tone: 'destructive' }))) return;
    try {
      await adminMagicResizeDebugService.deleteHistory(id);
      loadHistory(historyPage);
    } catch (err) {
      console.error('Failed to delete history:', err);
    }
  };

  // Toggle result expansion
  const toggleResult = (index: number) => {
    setExpandedResults(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Format aspect ratio
  const formatAR = (ratio: number | null | undefined) => {
    if (ratio == null) return '-';
    return ratio.toFixed(3);
  };

  // Format time
  const formatTime = (ms: number | null | undefined) => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  // Render final crop/stretch versions for a Gemini step
  const renderFinalVersions = (
    stepData: { final_crop?: FinalVersionResult; final_stretch?: FinalVersionResult } | undefined,
    targetSize: { width: number; height: number }
  ) => {
    if (!stepData?.final_crop && !stepData?.final_stretch) return null;
    return (
      <div className="my-3 rounded-tasky-xs border border-amber-200 bg-amber-50 p-2.5">
        <div className="mb-2 text-xs font-semibold text-amber-800">Final Versions → {targetSize.width}x{targetSize.height}</div>
        <div className="grid grid-cols-2 gap-3 max-[600px]:grid-cols-1">
          <div className={FINAL_VERSION_CARD}>
            <ComparisonLabel>Crop</ComparisonLabel>
            {stepData?.final_crop?.success && stepData.final_crop.image_base64 ? (
              <ComparisonImage src={stepData.final_crop.image_base64} alt="Crop" />
            ) : (
              <PlaceholderImage>{stepData?.final_crop?.error || 'N/A'}</PlaceholderImage>
            )}
            <ComparisonInfo>
              {stepData?.final_crop?.width}x{stepData?.final_crop?.height}
              {stepData?.final_crop?.crop_region && (
                <><br />Crop: {stepData.final_crop.crop_region}</>
              )}
            </ComparisonInfo>
          </div>
          <div className={FINAL_VERSION_CARD}>
            <ComparisonLabel>Stretch</ComparisonLabel>
            {stepData?.final_stretch?.success && stepData.final_stretch.image_base64 ? (
              <ComparisonImage src={stepData.final_stretch.image_base64} alt="Stretch" />
            ) : (
              <PlaceholderImage>{stepData?.final_stretch?.error || 'N/A'}</PlaceholderImage>
            )}
            <ComparisonInfo>
              {stepData?.final_stretch?.width}x{stepData?.final_stretch?.height}
              {stepData?.final_stretch?.source_size && (
                <><br />From: {stepData.final_stretch.source_size}</>
              )}
            </ComparisonInfo>
          </div>
        </div>
      </div>
    );
  };

  // Strategy label
  const strategyLabel = (strategy: string) => {
    switch (strategy) {
      case 'gemini': return 'Gemini AI';
      case 'center_crop_extreme_ratio': return 'CenterCrop (extreme ratio)';
      case 'center_crop_fallback': return 'CenterCrop (fallback)';
      case 'no_crop_needed': return 'No Crop Needed';
      default: return strategy;
    }
  };

  return (
    <div className="max-w-[1400px] p-6">
      <div className="mb-6">
        <h2 className="mb-1 mt-0 text-2xl font-semibold text-gray-900">Magic Resize Debug</h2>
        <p className="m-0 text-sm text-gray-500">Gemini AI / CenterCrop processing comparison</p>
      </div>

      {/* Source Image Input */}
      <TaskyCard variant="bordered" padding="sm" className="mb-4">
        <h3 className="mb-3 mt-0 text-base font-semibold text-gray-900">Source Image</h3>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Image ID:</label>
          <TaskyInput
            type="number"
            value={imageId}
            onChange={e => setImageId(e.target.value)}
            placeholder="generated_images ID"
            className="h-9 w-[200px] px-2.5 text-sm"
          />
        </div>
        {results?.source_image && (
          <div className="mt-3 flex items-start gap-3">
            <img
              src={results.source_image.image_base64}
              alt="Source"
              className="max-h-[200px] max-w-[200px] rounded-tasky-xs border border-gray-200 object-contain"
            />
            <div className="flex flex-col gap-1 text-[13px] text-gray-500">
              <span>{results.source_image.width} x {results.source_image.height}</span>
              <span>AR: {formatAR(results.source_image.aspect_ratio)}</span>
              <span>Size: {Math.round((results.source_image.file_size || 0) / 1024)}KB</span>
            </div>
          </div>
        )}
      </TaskyCard>

      {/* Target Size Selection */}
      <TaskyCard variant="bordered" padding="sm" className="mb-4">
        <h3 className="mb-3 mt-0 text-base font-semibold text-gray-900">Target Sizes</h3>

        {/* Presets */}
        {presetCategories.length > 0 && (
          <div className="mb-3">
            <SubTitle>Presets</SubTitle>
            <div className="flex flex-col gap-2">
              {presetCategories.map(cat => (
                <div key={cat.id}>
                  <div className="mb-1 text-xs font-semibold text-gray-700">{cat.name}</div>
                  <div className="flex flex-wrap gap-1">
                    {cat.presets?.filter(p => p.is_active).map(preset => {
                      const isSelected = selectedSizes.some(
                        s => s.width === preset.width && s.height === preset.height
                      );
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => handleAddPresetSize(preset.width, preset.height)}
                          disabled={isSelected}
                          className={cn(
                            'rounded-tasky-xs border px-2 py-1 text-xs transition-colors',
                            isSelected
                              ? 'cursor-default border-primary bg-primary/10 text-primary'
                              : 'cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-primary',
                          )}
                        >
                          {preset.name} ({preset.width}x{preset.height})
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom Size */}
        <div className="mb-3">
          <SubTitle>Custom</SubTitle>
          <div className="flex items-center gap-2">
            <TaskyInput
              type="number"
              value={customWidth}
              onChange={e => setCustomWidth(e.target.value)}
              placeholder="W"
              className="h-9 w-20 px-2.5 text-sm"
            />
            <span className="mx-1">x</span>
            <TaskyInput
              type="number"
              value={customHeight}
              onChange={e => setCustomHeight(e.target.value)}
              placeholder="H"
              className="h-9 w-20 px-2.5 text-sm"
            />
            <TaskyButton size="sm" onClick={handleAddCustomSize}>Add</TaskyButton>
          </div>
        </div>

        {/* Selected Sizes */}
        {selectedSizes.length > 0 && (
          <div>
            <SubTitle>Selected ({selectedSizes.length})</SubTitle>
            <div className="flex flex-wrap gap-1.5">
              {selectedSizes.map((size, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1 rounded-tasky-xs bg-gray-100 px-2 py-1 text-xs text-gray-700"
                >
                  {size.width}x{size.height}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRemoveSize(i)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleRemoveSize(i); }}
                    className="cursor-pointer text-sm leading-none text-gray-400 hover:text-destructive"
                  >
                    &times;
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </TaskyCard>

      {/* Execute Button */}
      <div className="mb-4 flex items-center gap-3">
        <TaskyButton
          onClick={handleExecute}
          disabled={isExecuting || !imageId || selectedSizes.length === 0}
          className="px-6 text-[15px] font-semibold"
        >
          {isExecuting ? 'Processing...' : 'Run Debug'}
        </TaskyButton>
        {isExecuting && <span className="text-[13px] text-gray-500">Gemini API processing may take time...</span>}
      </div>

      {/* Error */}
      {error && <ErrorBox>{error}</ErrorBox>}

      {/* Results */}
      {results && (
        <div>
          <h3 className="mb-3 mt-0 text-base font-semibold text-gray-900">Results ({results.results.length} sizes)</h3>
          {results.results.map((result, index) => (
            <div key={index} className="mb-3 overflow-hidden rounded-tasky-sm border border-gray-200 bg-white">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleResult(index)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleResult(index); }}
                className="cursor-pointer bg-gray-50 px-4 py-3 hover:bg-gray-100"
              >
                <div className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">
                  <span className="text-xs text-gray-500">{expandedResults.has(index) ? '▼' : '▶'}</span>
                  {result.target_size.width} x {result.target_size.height}
                  <TaskyBadge
                    variant="secondary"
                    className={cn(
                      'px-2 text-[11px]',
                      STRATEGY_BADGE_CLASS[result.strategy_used] ?? 'bg-amber-100 text-amber-800',
                    )}
                  >
                    {strategyLabel(result.strategy_used)}
                  </TaskyBadge>
                  <span className="text-xs font-normal text-gray-500">{formatTime(result.total_processing_time_ms)}</span>
                  {result.error && (
                    <TaskyBadge variant="destructive" className="px-2 text-[11px]">Error</TaskyBadge>
                  )}
                </div>
              </div>

              {expandedResults.has(index) && (
                <div className="border-t border-gray-200 p-4">
                  {result.error && <ErrorBox>{result.error}</ErrorBox>}

                  {/* Aspect Ratio Info */}
                  <div className="mb-4 flex gap-6">
                    <div>
                      <div className="mb-0.5 text-[11px] font-medium uppercase text-gray-400">Source AR</div>
                      <div className="text-sm font-medium text-gray-900">{formatAR(result.source_aspect_ratio)}</div>
                    </div>
                    <div>
                      <div className="mb-0.5 text-[11px] font-medium uppercase text-gray-400">Target AR</div>
                      <div className="text-sm font-medium text-gray-900">{formatAR(result.target_aspect_ratio)}</div>
                    </div>
                    <div>
                      <div className="mb-0.5 text-[11px] font-medium uppercase text-gray-400">Differs</div>
                      <div className="text-sm font-medium text-gray-900">{result.aspect_ratio_differs ? 'Yes' : 'No'}</div>
                    </div>
                  </div>

                  {/* Image Comparison */}
                  <div className="mb-4 grid grid-cols-4 gap-3 max-[1000px]:grid-cols-2">
                    {/* Source */}
                    <div className={COMPARISON_CARD}>
                      <ComparisonLabel>Source</ComparisonLabel>
                      {results.source_image?.image_base64 && (
                        <ComparisonImage src={results.source_image.image_base64} alt="Source" />
                      )}
                      <ComparisonInfo>
                        {results.source_image?.width}x{results.source_image?.height}
                        <br />AR: {formatAR(results.source_image?.aspect_ratio)}
                      </ComparisonInfo>
                    </div>

                    {/* Gemini Result */}
                    <div className={COMPARISON_CARD}>
                      <ComparisonLabel>
                        Gemini
                        {result.steps.gemini.attempted ? (
                          result.steps.gemini.success ?
                            <StatusDot status="success" /> :
                            <StatusDot status="error" />
                        ) : (
                          <StatusDot status="skipped" />
                        )}
                      </ComparisonLabel>
                      {result.steps.gemini.image_base64 ? (
                        <ComparisonImage src={result.steps.gemini.image_base64} alt="Gemini" />
                      ) : (
                        <PlaceholderImage>
                          {result.steps.gemini.reason || result.steps.gemini.error || 'N/A'}
                        </PlaceholderImage>
                      )}
                      <ComparisonInfo>
                        {result.steps.gemini.result_size
                          ? `${result.steps.gemini.result_size.width}x${result.steps.gemini.result_size.height}`
                          : '-'}
                        <br />AR: {formatAR(result.steps.gemini.result_aspect_ratio)}
                        <br />{formatTime(result.steps.gemini.processing_time_ms)}
                      </ComparisonInfo>
                    </div>

                    {/* CenterCrop Result */}
                    <div className={COMPARISON_CARD}>
                      <ComparisonLabel>CenterCrop</ComparisonLabel>
                      {result.steps.center_crop.image_base64 ? (
                        <ComparisonImage src={result.steps.center_crop.image_base64} alt="CenterCrop" />
                      ) : (
                        <PlaceholderImage>N/A</PlaceholderImage>
                      )}
                      <ComparisonInfo>
                        {result.steps.center_crop.result_size
                          ? `${result.steps.center_crop.result_size.width}x${result.steps.center_crop.result_size.height}`
                          : '-'}
                        <br />Dir: {result.steps.center_crop.crop_direction || '-'}
                        <br />{formatTime(result.steps.center_crop.processing_time_ms)}
                      </ComparisonInfo>
                    </div>

                    {/* Final Result */}
                    <div className={COMPARISON_CARD}>
                      <ComparisonLabel>Final ({result.target_size.width}x{result.target_size.height})</ComparisonLabel>
                      {result.steps.final_resize.image_base64 ? (
                        <ComparisonImage src={result.steps.final_resize.image_base64} alt="Final" />
                      ) : (
                        <PlaceholderImage>Error</PlaceholderImage>
                      )}
                      <ComparisonInfo>
                        {result.steps.final_resize.result_size
                          ? `${result.steps.final_resize.result_size.width}x${result.steps.final_resize.result_size.height}`
                          : '-'}
                        <br />{formatTime(result.steps.final_resize.processing_time_ms)}
                      </ComparisonInfo>
                    </div>
                  </div>

                  {/* Existing Gemini Final Versions */}
                  {result.steps.gemini.success && renderFinalVersions(result.steps.gemini, result.target_size)}

                  {/* Gemini Details */}
                  {result.steps.gemini.attempted && (
                    <div className="rounded-tasky-xs border border-sky-200 bg-sky-50 p-3">
                      <SubTitle>Gemini Details</SubTitle>
                      <div className={DETAIL_GRID}>
                        {result.steps.gemini.aspect_ratio_deviation_percent != null && (
                          <div>
                            <DetailLabel>AR Deviation</DetailLabel>
                            <DetailValue
                              highlight={result.steps.gemini.aspect_ratio_deviation_percent > 5}
                            >
                              {result.steps.gemini.aspect_ratio_deviation_percent}%
                              {result.steps.gemini.needed_center_crop_correction && ' (corrected)'}
                            </DetailValue>
                          </div>
                        )}
                        {result.steps.gemini.canvas_size && (
                          <div>
                            <DetailLabel>Canvas</DetailLabel>
                            <DetailValue>
                              {result.steps.gemini.canvas_size.width}x{result.steps.gemini.canvas_size.height}
                            </DetailValue>
                          </div>
                        )}
                        {result.steps.gemini.usage_metadata && (
                          <>
                            <div>
                              <DetailLabel>Prompt Tokens</DetailLabel>
                              <DetailValue>{result.steps.gemini.usage_metadata.prompt_tokens}</DetailValue>
                            </div>
                            <div>
                              <DetailLabel>Completion</DetailLabel>
                              <DetailValue>{result.steps.gemini.usage_metadata.completion_tokens}</DetailValue>
                            </div>
                            <div>
                              <DetailLabel>Image Tokens</DetailLabel>
                              <DetailValue>{result.steps.gemini.usage_metadata.image_tokens}</DetailValue>
                            </div>
                            <div>
                              <DetailLabel>Total Tokens</DetailLabel>
                              <DetailValue>{result.steps.gemini.usage_metadata.total_tokens}</DetailValue>
                            </div>
                          </>
                        )}
                      </div>
                      {result.steps.gemini.prompt && (
                        <div className="mt-2">
                          <DetailLabel>Prompt</DetailLabel>
                          <PromptText>{result.steps.gemini.prompt}</PromptText>
                        </div>
                      )}
                      {result.steps.gemini.error && (
                        <ErrorBox className="mt-2">
                          Gemini Error: {result.steps.gemini.error}
                        </ErrorBox>
                      )}
                    </div>
                  )}

                  {/* Blue Margin Method */}
                  {result.steps.gemini_blue_margin && (
                    <div className="mt-3 rounded-tasky-xs border border-sky-200 bg-sky-50 p-3">
                      <div className="mb-3">
                        <SubTitle>
                          <span className={cn(SECTION_BADGE, 'bg-sky-500')}>Blue Margin</span>
                          Method
                        </SubTitle>
                      </div>

                      {result.steps.gemini_blue_margin.error ? (
                        <ErrorBox>{result.steps.gemini_blue_margin.error}</ErrorBox>
                      ) : !result.steps.gemini_blue_margin.attempted ? (
                        <div className="flex h-20 items-center justify-center rounded-tasky-xs border border-dashed border-sky-200 bg-gray-50 text-center text-[13px] text-gray-500">
                          Skipped: {result.steps.gemini_blue_margin.reason || 'Not attempted'}
                        </div>
                      ) : (
                        <>
                          {/* Image Comparison Grid */}
                          <div className="mb-3 grid grid-cols-3 gap-3 max-[1000px]:grid-cols-1">
                            {/* Canvas Input */}
                            <div className={BLUE_MARGIN_CARD}>
                              <ComparisonLabel>Canvas Input</ComparisonLabel>
                              {result.steps.gemini_blue_margin.canvas_image_base64 ? (
                                <ComparisonImage
                                  src={result.steps.gemini_blue_margin.canvas_image_base64}
                                  alt="Canvas Input"
                                />
                              ) : (
                                <PlaceholderImage>N/A</PlaceholderImage>
                              )}
                              <ComparisonInfo>
                                {result.steps.gemini_blue_margin.canvas_size
                                  ? `${result.steps.gemini_blue_margin.canvas_size.width}x${result.steps.gemini_blue_margin.canvas_size.height}`
                                  : '-'}
                              </ComparisonInfo>
                            </div>

                            {/* Gemini Raw */}
                            <div className={BLUE_MARGIN_CARD}>
                              <ComparisonLabel>Gemini Raw</ComparisonLabel>
                              {result.steps.gemini_blue_margin.raw_image_base64 ? (
                                <ComparisonImage
                                  src={result.steps.gemini_blue_margin.raw_image_base64}
                                  alt="Gemini Raw"
                                />
                              ) : (
                                <PlaceholderImage>N/A</PlaceholderImage>
                              )}
                              <ComparisonInfo>
                                {result.steps.gemini_blue_margin.raw_result_size
                                  ? `${result.steps.gemini_blue_margin.raw_result_size.width}x${result.steps.gemini_blue_margin.raw_result_size.height}`
                                  : '-'}
                              </ComparisonInfo>
                            </div>

                            {/* After Crop */}
                            <div className={BLUE_MARGIN_CARD}>
                              <ComparisonLabel>After Crop</ComparisonLabel>
                              {result.steps.gemini_blue_margin.cropped_image_base64 ? (
                                <ComparisonImage
                                  src={result.steps.gemini_blue_margin.cropped_image_base64}
                                  alt="After Crop"
                                />
                              ) : (
                                <PlaceholderImage>N/A</PlaceholderImage>
                              )}
                              <ComparisonInfo>
                                {result.steps.gemini_blue_margin.cropped_result_size
                                  ? `${result.steps.gemini_blue_margin.cropped_result_size.width}x${result.steps.gemini_blue_margin.cropped_result_size.height}`
                                  : '-'}
                                {result.steps.gemini_blue_margin.aspect_ratio_deviation_percent != null && (
                                  <>
                                    <br />
                                    Deviation:{' '}
                                    <span
                                      className={
                                        result.steps.gemini_blue_margin.aspect_ratio_deviation_percent > 5
                                          ? 'font-bold text-destructive'
                                          : 'font-normal text-gray-500'
                                      }
                                    >
                                      {result.steps.gemini_blue_margin.aspect_ratio_deviation_percent}%
                                    </span>
                                  </>
                                )}
                              </ComparisonInfo>
                            </div>
                          </div>

                          {/* Blue Margin Final Versions */}
                          {result.steps.gemini_blue_margin.success && renderFinalVersions(result.steps.gemini_blue_margin, result.target_size)}

                          {/* Details Grid */}
                          {(result.steps.gemini_blue_margin.aspect_ratio_deviation_percent != null ||
                            result.steps.gemini_blue_margin.content_area ||
                            result.steps.gemini_blue_margin.usage_metadata) && (
                            <div className={DETAIL_GRID_NARROW}>
                              {result.steps.gemini_blue_margin.aspect_ratio_deviation_percent != null && (
                                <div>
                                  <DetailLabel>AR Deviation</DetailLabel>
                                  <DetailValue
                                    highlight={
                                      result.steps.gemini_blue_margin.aspect_ratio_deviation_percent > 5
                                    }
                                  >
                                    {result.steps.gemini_blue_margin.aspect_ratio_deviation_percent}%
                                  </DetailValue>
                                </div>
                              )}
                              {result.steps.gemini_blue_margin.result_aspect_ratio != null && (
                                <div>
                                  <DetailLabel>Result AR</DetailLabel>
                                  <DetailValue>{result.steps.gemini_blue_margin.result_aspect_ratio.toFixed(3)}</DetailValue>
                                </div>
                              )}
                              {result.steps.gemini_blue_margin.processing_time_ms && (
                                <div>
                                  <DetailLabel>Processing Time</DetailLabel>
                                  <DetailValue>{formatTime(result.steps.gemini_blue_margin.processing_time_ms)}</DetailValue>
                                </div>
                              )}
                              {result.steps.gemini_blue_margin.content_area && (
                                <>
                                  <div>
                                    <DetailLabel>Content X</DetailLabel>
                                    <DetailValue>{result.steps.gemini_blue_margin.content_area.x}</DetailValue>
                                  </div>
                                  <div>
                                    <DetailLabel>Content Y</DetailLabel>
                                    <DetailValue>{result.steps.gemini_blue_margin.content_area.y}</DetailValue>
                                  </div>
                                  <div>
                                    <DetailLabel>Content Width</DetailLabel>
                                    <DetailValue>{result.steps.gemini_blue_margin.content_area.width}</DetailValue>
                                  </div>
                                  <div>
                                    <DetailLabel>Content Height</DetailLabel>
                                    <DetailValue>{result.steps.gemini_blue_margin.content_area.height}</DetailValue>
                                  </div>
                                </>
                              )}
                              {result.steps.gemini_blue_margin.usage_metadata && (
                                <>
                                  <div>
                                    <DetailLabel>Prompt Tokens</DetailLabel>
                                    <DetailValue>{result.steps.gemini_blue_margin.usage_metadata.prompt_tokens}</DetailValue>
                                  </div>
                                  <div>
                                    <DetailLabel>Completion</DetailLabel>
                                    <DetailValue>{result.steps.gemini_blue_margin.usage_metadata.completion_tokens}</DetailValue>
                                  </div>
                                  <div>
                                    <DetailLabel>Image Tokens</DetailLabel>
                                    <DetailValue>{result.steps.gemini_blue_margin.usage_metadata.image_tokens}</DetailValue>
                                  </div>
                                  <div>
                                    <DetailLabel>Total Tokens</DetailLabel>
                                    <DetailValue>{result.steps.gemini_blue_margin.usage_metadata.total_tokens}</DetailValue>
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          {/* Prompt */}
                          {result.steps.gemini_blue_margin.prompt && (
                            <div className="mt-2">
                              <DetailLabel>Prompt</DetailLabel>
                              <PromptText>{result.steps.gemini_blue_margin.prompt}</PromptText>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Approach A: White Canvas + AR Config */}
                  {result.steps.gemini_ar_config && (
                    <div className="mt-3 rounded-tasky-xs border border-green-200 border-l-4 border-l-success bg-green-50 p-3">
                      <div className="mb-3">
                        <SubTitle>
                          <span className={cn(SECTION_BADGE, 'bg-success')}>A: Canvas + AR</span>
                          Method
                        </SubTitle>
                      </div>

                      {result.steps.gemini_ar_config.error ? (
                        <ErrorBox>{result.steps.gemini_ar_config.error}</ErrorBox>
                      ) : !result.steps.gemini_ar_config.attempted ? (
                        <div className="flex h-20 items-center justify-center rounded-tasky-xs border border-dashed border-sky-200 bg-gray-50 text-center text-[13px] text-gray-500">
                          Skipped: Not attempted
                        </div>
                      ) : (
                        <>
                          {/* Result Image Card */}
                          <div className="mb-3 grid grid-cols-1 gap-3">
                            <div className={APPROACH_CARD}>
                              <ComparisonLabel>Result Image</ComparisonLabel>
                              {result.steps.gemini_ar_config.image_base64 ? (
                                <ComparisonImage
                                  src={result.steps.gemini_ar_config.image_base64}
                                  alt="Approach A Result"
                                />
                              ) : (
                                <PlaceholderImage>N/A</PlaceholderImage>
                              )}
                              <ComparisonInfo>
                                {result.steps.gemini_ar_config.result_size
                                  ? `${result.steps.gemini_ar_config.result_size.width}x${result.steps.gemini_ar_config.result_size.height}`
                                  : '-'}
                              </ComparisonInfo>
                            </div>
                          </div>

                          {/* Approach A Final Versions */}
                          {result.steps.gemini_ar_config.success && renderFinalVersions(result.steps.gemini_ar_config, result.target_size)}

                          {/* Details Grid */}
                          <div className={DETAIL_GRID_NARROW}>
                            {result.steps.gemini_ar_config.closest_gemini_ratio && (
                              <div>
                                <DetailLabel>Closest Ratio</DetailLabel>
                                <DetailValue>{result.steps.gemini_ar_config.closest_gemini_ratio}</DetailValue>
                              </div>
                            )}
                            {result.steps.gemini_ar_config.ar_deviation != null && (
                              <div>
                                <DetailLabel>AR Deviation</DetailLabel>
                                <DetailValue
                                  highlight={result.steps.gemini_ar_config.ar_deviation > 5}
                                >
                                  {result.steps.gemini_ar_config.ar_deviation}%
                                </DetailValue>
                              </div>
                            )}
                            {result.steps.gemini_ar_config.processing_time_s && (
                              <div>
                                <DetailLabel>Processing Time</DetailLabel>
                                <DetailValue>{result.steps.gemini_ar_config.processing_time_s.toFixed(1)}s</DetailValue>
                              </div>
                            )}
                            {result.steps.gemini_ar_config.canvas_size && (
                              <div>
                                <DetailLabel>Canvas Size</DetailLabel>
                                <DetailValue>{result.steps.gemini_ar_config.canvas_size}</DetailValue>
                              </div>
                            )}
                            {result.steps.gemini_ar_config.usage_metadata && (
                              <>
                                <div>
                                  <DetailLabel>Prompt Tokens</DetailLabel>
                                  <DetailValue>{result.steps.gemini_ar_config.usage_metadata.prompt_tokens}</DetailValue>
                                </div>
                                <div>
                                  <DetailLabel>Completion</DetailLabel>
                                  <DetailValue>{result.steps.gemini_ar_config.usage_metadata.completion_tokens}</DetailValue>
                                </div>
                                <div>
                                  <DetailLabel>Image Tokens</DetailLabel>
                                  <DetailValue>{result.steps.gemini_ar_config.usage_metadata.image_tokens}</DetailValue>
                                </div>
                                <div>
                                  <DetailLabel>Total Tokens</DetailLabel>
                                  <DetailValue>{result.steps.gemini_ar_config.usage_metadata.total_tokens}</DetailValue>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Prompt */}
                          {result.steps.gemini_ar_config.prompt && (
                            <div className="mt-2">
                              <DetailLabel>Prompt</DetailLabel>
                              <PromptText>{result.steps.gemini_ar_config.prompt}</PromptText>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Approach B: Image Only + AR Config */}
                  {result.steps.gemini_image_only && (
                    <div className="mt-3 rounded-tasky-xs border border-purple-200 border-l-4 border-l-purple bg-purple-50 p-3">
                      <div className="mb-3">
                        <SubTitle>
                          <span className={cn(SECTION_BADGE, 'bg-purple')}>B: Image Only</span>
                          Method
                        </SubTitle>
                      </div>

                      {result.steps.gemini_image_only.error ? (
                        <ErrorBox>{result.steps.gemini_image_only.error}</ErrorBox>
                      ) : !result.steps.gemini_image_only.attempted ? (
                        <div className="flex h-20 items-center justify-center rounded-tasky-xs border border-dashed border-sky-200 bg-gray-50 text-center text-[13px] text-gray-500">
                          Skipped: Not attempted
                        </div>
                      ) : (
                        <>
                          {/* Result Image Card */}
                          <div className="mb-3 grid grid-cols-1 gap-3">
                            <div className={APPROACH_CARD}>
                              <ComparisonLabel>Result Image</ComparisonLabel>
                              {result.steps.gemini_image_only.image_base64 ? (
                                <ComparisonImage
                                  src={result.steps.gemini_image_only.image_base64}
                                  alt="Approach B Result"
                                />
                              ) : (
                                <PlaceholderImage>N/A</PlaceholderImage>
                              )}
                              <ComparisonInfo>
                                {result.steps.gemini_image_only.result_size
                                  ? `${result.steps.gemini_image_only.result_size.width}x${result.steps.gemini_image_only.result_size.height}`
                                  : '-'}
                              </ComparisonInfo>
                            </div>
                          </div>

                          {/* Approach B Final Versions */}
                          {result.steps.gemini_image_only.success && renderFinalVersions(result.steps.gemini_image_only, result.target_size)}

                          {/* Details Grid */}
                          <div className={DETAIL_GRID_NARROW}>
                            {result.steps.gemini_image_only.closest_gemini_ratio && (
                              <div>
                                <DetailLabel>Closest Ratio</DetailLabel>
                                <DetailValue>{result.steps.gemini_image_only.closest_gemini_ratio}</DetailValue>
                              </div>
                            )}
                            {result.steps.gemini_image_only.ar_deviation != null && (
                              <div>
                                <DetailLabel>AR Deviation</DetailLabel>
                                <DetailValue
                                  highlight={result.steps.gemini_image_only.ar_deviation > 5}
                                >
                                  {result.steps.gemini_image_only.ar_deviation}%
                                </DetailValue>
                              </div>
                            )}
                            {result.steps.gemini_image_only.processing_time_s && (
                              <div>
                                <DetailLabel>Processing Time</DetailLabel>
                                <DetailValue>{result.steps.gemini_image_only.processing_time_s.toFixed(1)}s</DetailValue>
                              </div>
                            )}
                            {result.steps.gemini_image_only.usage_metadata && (
                              <>
                                <div>
                                  <DetailLabel>Prompt Tokens</DetailLabel>
                                  <DetailValue>{result.steps.gemini_image_only.usage_metadata.prompt_tokens}</DetailValue>
                                </div>
                                <div>
                                  <DetailLabel>Completion</DetailLabel>
                                  <DetailValue>{result.steps.gemini_image_only.usage_metadata.completion_tokens}</DetailValue>
                                </div>
                                <div>
                                  <DetailLabel>Image Tokens</DetailLabel>
                                  <DetailValue>{result.steps.gemini_image_only.usage_metadata.image_tokens}</DetailValue>
                                </div>
                                <div>
                                  <DetailLabel>Total Tokens</DetailLabel>
                                  <DetailValue>{result.steps.gemini_image_only.usage_metadata.total_tokens}</DetailValue>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Prompt */}
                          {result.steps.gemini_image_only.prompt && (
                            <div className="mt-2">
                              <DetailLabel>Prompt</DetailLabel>
                              <PromptText>{result.steps.gemini_image_only.prompt}</PromptText>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* History Panel */}
      <div className="mt-8 rounded-tasky-sm border border-gray-200 bg-gray-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="m-0 text-base font-semibold text-gray-800">History ({historyTotal})</h3>
          <TaskyButton
            variant="secondary"
            size="sm"
            onClick={() => loadHistory(historyPage)}
            disabled={isLoadingHistory}
            className={SMALL_OUTLINE_BUTTON}
          >
            {isLoadingHistory ? 'Loading...' : 'Refresh'}
          </TaskyButton>
        </div>
        {historyItems.length > 0 ? (
          <>
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  <th className={HISTORY_HEAD_CELL}>Date</th>
                  <th className={HISTORY_HEAD_CELL}>Image ID</th>
                  <th className={HISTORY_HEAD_CELL}>Source</th>
                  <th className={HISTORY_HEAD_CELL}>Targets</th>
                  <th className={HISTORY_HEAD_CELL}>Methods</th>
                  <th className={HISTORY_HEAD_CELL}>Time</th>
                  <th className={HISTORY_HEAD_CELL}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {historyItems.map(item => (
                  <tr key={item.id} className="hover:bg-blue-50">
                    <td className={HISTORY_CELL}>{new Date(item.created_at).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td className={HISTORY_CELL}>{item.image_id}</td>
                    <td className={HISTORY_CELL}>{item.source_width}x{item.source_height}</td>
                    <td className={HISTORY_CELL}>
                      {item.target_sizes.map((s, i) => (
                        <span
                          key={i}
                          className="my-px mx-0.5 inline-block rounded-tasky-xs bg-gray-200 px-1.5 py-px text-[11px] text-gray-700"
                        >
                          {s.width}x{s.height}
                        </span>
                      ))}
                    </td>
                    <td className={HISTORY_CELL}>
                      {item.result_summary?.results?.map((r, i) => (
                        <div key={i} className="my-px inline-flex gap-0.5">
                          {r.methods.gemini && <MethodBadge success={r.methods.gemini.success}>G</MethodBadge>}
                          {r.methods.gemini_blue_margin && <MethodBadge success={r.methods.gemini_blue_margin.success}>B</MethodBadge>}
                          {r.methods.gemini_ar_config && <MethodBadge success={r.methods.gemini_ar_config.success}>A</MethodBadge>}
                          {r.methods.gemini_image_only && <MethodBadge success={r.methods.gemini_image_only.success}>I</MethodBadge>}
                        </div>
                      ))}
                    </td>
                    <td className={HISTORY_CELL}>{item.total_processing_time_ms ? `${(item.total_processing_time_ms / 1000).toFixed(1)}s` : '-'}</td>
                    <td className={HISTORY_CELL}>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleLoadHistoryDetail(item.id)}
                          disabled={isLoadingHistoryDetail && loadedHistoryId === item.id}
                          title="Load result"
                          className="cursor-pointer rounded-tasky-xs border border-primary bg-primary/10 px-2 py-0.5 text-[11px] text-primary transition-colors hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isLoadingHistoryDetail && loadedHistoryId === item.id ? '...' : 'Load'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteHistory(item.id)}
                          title="Delete"
                          className="cursor-pointer rounded-tasky-xs border border-destructive bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive transition-colors hover:bg-destructive/20"
                        >
                          Del
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {historyLastPage > 1 && (
              <div className="mt-3 flex items-center justify-center gap-3">
                <TaskyButton
                  variant="secondary"
                  size="sm"
                  onClick={() => loadHistory(historyPage - 1)}
                  disabled={historyPage <= 1}
                  className={SMALL_OUTLINE_BUTTON}
                >
                  Prev
                </TaskyButton>
                <span className="text-[13px] text-gray-500">{historyPage} / {historyLastPage}</span>
                <TaskyButton
                  variant="secondary"
                  size="sm"
                  onClick={() => loadHistory(historyPage + 1)}
                  disabled={historyPage >= historyLastPage}
                  className={SMALL_OUTLINE_BUTTON}
                >
                  Next
                </TaskyButton>
              </div>
            )}
          </>
        ) : (
          <div className="p-6 text-center text-sm text-gray-400">
            {isLoadingHistory ? 'Loading...' : 'No history yet'}
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicResizeDebugPage;
